import { FC } from 'react';
import { Box, HStack, Image, useDisclosure, VStack } from '@chakra-ui/react';
import {
  calculateInvoiceTaskTotals,
  calculateInvoiceTotals,
  InvoiceTask,
} from '@shinetools/invoicing-rules';

import { TransactionFeeType } from '__generated__/GQL';
import creditCardImg from 'assets/brand/credit-card.png';
import { getFeesForType } from 'common/fees';
import { useCompanySettings } from 'common/hooks/useCompanySettings';
import Button from 'components/_core/Button';
import SunshineCard from 'components/_core/SunshineCard';
import SunshineIcon from 'components/_core/SunshineIcon';
import SunshineModal from 'components/_core/SunshineModal';
import SunshineTag from 'components/_core/SunshineTag';
import Text from 'components/_core/Text';
import useToast from 'components/ToastProvider/useToast';
import { DEFAULT_MAX_CREDIT_CARD_PAYMENT } from 'features/Work/pages/Invoices/components/FormLayout/constants';
import { Invoice } from 'features/Work/pages/Invoices/hooks/queries/useGetInvoice';

import { useUpdateInvoicePaymentMethods } from '../../hooks/useUpdateInvoicePaymentMethods';
import locales from './locales';

interface PaymentMethodsProps {
  invoiceData: Pick<Invoice, 'invoiceId' | 'allowCreditCardPayment'> & {
    tasks: InvoiceTask[];
  };
}

export const PaymentMethods: FC<PaymentMethodsProps> = ({ invoiceData }) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { metadata } = useCompanySettings();
  const { showToast } = useToast();

  const { loading, updateInvoicePaymentMethods } =
    useUpdateInvoicePaymentMethods({
      allowCreditCardPayment: true,
      invoiceId: invoiceData.invoiceId,
    });

  const onSubmit = () => {
    updateInvoicePaymentMethods({
      onCompleted: () => {
        showToast({
          message: locales.toast.success,
          type: 'success',
        });
      },
      onError: () => {
        showToast({
          message: locales.toast.error,
          type: 'error',
        });
      },
    });
  };

  const formattedTasks = invoiceData.tasks.map((task) => ({
    ...task,
    ...calculateInvoiceTaskTotals(task),
  }));

  const { totalAmountAfterTaxes } = calculateInvoiceTotals(formattedTasks);
  const maxInvoiceCardPayment =
    metadata?.maxInvoiceCardPayment ?? DEFAULT_MAX_CREDIT_CARD_PAYMENT;

  if (
    invoiceData.allowCreditCardPayment ||
    totalAmountAfterTaxes >= maxInvoiceCardPayment
  ) {
    return null;
  }

  return (
    <>
      <SunshineCard onClick={onOpen}>
        <HStack>
          <VStack alignItems="start" gap="space-16">
            <SunshineTag colorScheme="grey" icon="flash" size="sm">
              {locales.badgeLabel}
            </SunshineTag>
            <SunshineCard.Title>{locales.cardTitle}</SunshineCard.Title>
          </VStack>

          <SunshineIcon color="light" name="chevron-right" size="icon-24" />
        </HStack>
      </SunshineCard>

      <SunshineModal isOpen={isOpen} onClose={onClose}>
        <SunshineModal.Header />

        <VStack gap="space-24" padding="space-32">
          <Image alt="" src={creditCardImg} width={140} />

          <Box>
            <SunshineModal.Title>{locales.modal.title}</SunshineModal.Title>

            <Text>{locales.modal.description}</Text>
          </Box>

          <Text size="xs" variant="light">
            {locales.formatString(locales.modal.fees, {
              cardPayinEuro: getFeesForType(TransactionFeeType.CardPayinEuro),
              cardPayinNonEuro: getFeesForType(
                TransactionFeeType.CardPayinNonEuro,
              ),
            })}
          </Text>
        </VStack>

        <SunshineModal.Footer>
          <Button
            isDisabled={loading}
            onClick={onClose}
            variant="inline-primary"
          >
            {locales.modal.cancel}
          </Button>

          <Button isLoading={loading} onClick={onSubmit}>
            {locales.modal.ctaText}
          </Button>
        </SunshineModal.Footer>
      </SunshineModal>
    </>
  );
};
